import React, { useContext, useState } from 'react';
import { Box, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { UIContext } from '../../../../context/UIContext';
import { SidekickTablesContext } from '../helpers/SidekickTablesContext';

interface DataTableProps<T> {
  isLoading: boolean;
  columns: GridColDef[];
  getRowId: (row: T) => string | number;
  dataQaId: string;
  showFirstLastButtons?: boolean;
  columnVisibilityModel?: Record<string, boolean>;
}

const DataTable = <T extends object>({
  isLoading,
  columns,
  getRowId,
  dataQaId,
  showFirstLastButtons = false,
  columnVisibilityModel,
}: DataTableProps<T>) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { isTablet, isMediumDevice } = useContext(UIContext);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const {
    processRowUpdate,
    handleRowModesModelChange,
    handleProcessRowUpdateError,
    rowModesModel,
    rows,
    gridApiRef,
  } = useContext(SidekickTablesContext);

  return (
    <DataGridPro
      rows={rows || []}
      editMode="row"
      apiRef={gridApiRef}
      getRowId={getRowId}
      columns={columns}
      pagination
      pageSizeOptions={[10, 25, 50]}
      paginationModel={paginationModel}
      onPaginationModelChange={(newPaginationModel) => setPaginationModel(newPaginationModel)}
      rowHeight={72}
      data-qa-id={dataQaId}
      rowModesModel={rowModesModel}
      onRowModesModelChange={handleRowModesModelChange}
      columnVisibilityModel={columnVisibilityModel}
      columnHeaderHeight={72}
      disableColumnMenu
      disableRowSelectionOnClick
      columnBuffer={4}
      disableVirtualization={process.env.NODE_ENV === 'test'}
      loading={isLoading}
      processRowUpdate={processRowUpdate}
      onProcessRowUpdateError={handleProcessRowUpdateError}
      slotProps={{
        pagination: {
          showFirstButton: showFirstLastButtons,
          showLastButton: showFirstLastButtons,
          labelRowsPerPage: t('components.table.rowsPerPage'),
          labelDisplayedRows: (paginationInfo) =>
            t('components.table.displayedRows', {
              from: paginationInfo.from,
              to: paginationInfo.to,
              count: paginationInfo.count,
            }),
        },
      }}
      sx={{
        '& .hideRightSeparator > .MuiDataGrid-columnSeparator': {
          display: 'none',
        },
        '& .MuiDataGrid-columnHeaderTitleContainer': {
          alignItems: 'center',
        },
        '.MuiDataGrid-iconButtonContainer': {
          visibility: 'visible',
        },
        '.MuiDataGrid-row.MuiDataGrid-row--lastVisible': {
          '.MuiDataGrid-cell': {
            borderBottom: 0,
          },
        },
        '& ::-webkit-scrollbar': {
          width: '6px',
          height: '6px',
        },
        '& .MuiDataGrid-virtualScroller': {
          overflowX: 'auto',
        },
        '& ::-webkit-scrollbar-track': {
          backgroundColor: theme?.palette?.grey?.['8p'],
          borderRadius: '10px',
        },
        '& ::-webkit-scrollbar-thumb': {
          borderRadius: '10px',
          backgroundColor: theme?.palette?.grey?.['50p'],
        },
        '.MuiDataGrid-cell--editing': {
          '&:first-of-type': {
            input: {
              pl: 0,
            },
          },
          input: {
            pl: 1.5,
          },
        },
        '.MuiDataGrid-columnHeader': {
          padding: '0 12px',
          ...(isTablet && {
            padding: '0 8px',
          }),
          '&:first-of-type': {
            pl: 2,
            ...(!isMediumDevice && {
              pl: 1,
            }),
          },
          '&:last-child': {
            pr: 2,
            ...(!isMediumDevice && {
              pr: 1,
            }),
          },
        },
        '.MuiDataGrid-cell': {
          padding: '0 12px',
          ...(isTablet && {
            padding: '0 8px',
          }),
          '&:first-of-type': {
            pl: 2,
            ...(!isMediumDevice && {
              pl: 1,
            }),
          },
          '&:has(.Mui-error)': {
            border: '2px solid #D32F2F',
          },
          '&:has(.Mui-focused):has(.Mui-error)': {
            outline: 'none',
          },
        },
        ...(rows?.length === 0 && {
          '& .MuiDataGrid-virtualScroller': {
            overflow: 'hidden',
          },
        }),
      }}
    />
  );
};

export default DataTable;
